// Angular
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

// Module
import { GlobalConfigurationRoutingModule } from './global-configuration-routing.module';
import { CvComponentLibraryModule } from '@cv-invision/cv.component-library.ux';
import { AgGridModule } from 'ag-grid-angular';
import { AceEditorModule } from 'ngx-ace-editor-wrapper';
import { ClipboardModule } from 'ngx-clipboard';
import { HttpCoreModule } from '@cv-invision/core';
import { TabViewModule } from 'primeng/tabview';
import { PanelModule } from 'primeng/panel';
import { DropdownModule } from 'primeng/dropdown';
import { GenericIntegrationsModule } from './generic-integrations/generic-integrations.module';

// Store
import { NgxsModule } from '@ngxs/store';
// Service
import { ComponentFactoryResolverService } from '@cv-invision/core';
// Components
import { ProductsComponent } from './products/products.component';
import { CreateUpdateProductsComponent } from './products/create-update-products/create-update-products.component';
import { ScopeOrderDocumentsComponent } from './products/components/scope-order-documents.component';

import { CustomActionProductsCellComponent } from './products/custom-grid-cell/custom-action-products-cell.component';
import { ProductsService } from './products/service/products.service';
import { GlobalConfigurationState } from './state/global-configuration.state';
import { CallbackLogsComponent } from './nexsys-monitor/callback-logs/callback-logs.component';
import { NexsysOrdersComponent } from './nexsys-monitor/nexsys-orders/nexsys-orders.component';
import { LogsDescriptionComponent } from './nexsys-monitor/callback-logs/logs-description/logs-description.component';
import { NexsysMonitorService } from './service/nexsys-monitor.service';
import { SoftwareCollectorPackagesComponent } from './software-collector-packages/software-collector-packages.component';
import {
	CustomActionSoftwareCollectorPackageCellComponent
} from './software-collector-packages/custom-grid-cell/custom-action-software-collector-package-cell.component';
import { SoftwareCollectorPackagesService } from './service/software-collector-packages.service';
import { CreateUpdatePackageComponent } from './software-collector-packages/create-update-package/create-update-package.component';
import { LenderComponent } from './lender/lender.component';
import { CustomActionLenderCellComponent } from './lender/custom-grid-cell/custom-action-lender-cell.component';
import { LenderService } from './service/lender.service';
import { CreateUpdateLenderComponent } from './lender/create-update-lender/create-update-lender.component';
import { NexsysMonitorHeaderComponent } from './nexsys-monitor/nexsys-monitor-header/nexsys-monitor-header.component';
import {
	CustomActionNexsysOrdersCellComponent
} from './nexsys-monitor/nexsys-orders/custom-action-grid-cell/custom-action-grid-cell.component';
import {
	CustomActionShowProductModalCellComponent
} from './nexsys-monitor/nexsys-orders/custom-action-grid-cell/custom-action-show-product-modal-cell.component';
import { NexsysLogsOrderComponent } from './nexsys-monitor/nexsys-logs-order/nexsys-logs-order.component';
import { LogsOrderDescriptionComponent } from './nexsys-monitor/nexsys-logs-order/logs-description/logs-description.component';

import {
	CustomActionRetryCellComponent
} from './nexsys-monitor/nexsys-logs-order/custom-action-grid-cell/custom-action-grid-cell.component';
import { RetryNexsysEventComponent } from './nexsys-monitor/nexsys-logs-order/retry-nexsys-event/retry-nexsys-event.component';
import { NexsysMonitorState } from './nexsys-monitor/state/nexsys-monitor.state';
import { ProductState } from './products/state/product.state';
import { OrdersLogComponent } from './nexsys-monitor/nexsys-orders/orders-log/orders-log.component';

import { CustomActionNexsysOrphanReadComponent } from './nexsys-monitor/custom-grid-cell/custom-action-nexsys-orphan-read.component';
import { CustomActionNexsysOrphanDownloadComponent } from './nexsys-monitor/custom-grid-cell/custom-action-nexsys-orphan-download.component';
import { CustomActionNexsysHTTPStatusCodeComponent } from './nexsys-monitor/custom-grid-cell/custom-action-nexsys-http-status-code.component';
import { NexsysDefaultSettingComponent } from './nexsys-mapper-types/nexsys-default-setting/nexsys-default-setting.component';
import { CIJobTypesComponent } from './ci-job-types/ci-job-types.component';
import { CustomActionCIJobTypesCellComponent } from './ci-job-types/custom-grid-cell/custom-action-ci-job-types-cell.component';
import { CreateUpdateCIJobTypeComponent } from './ci-job-types/create-update-ci-job-type/create-update-ci-job-type.component';

import { RequestInformationComponent } from './nexsys-monitor/callback-logs/request-information/request-information.component';
import {
	RequestInformationLogComponent
} from './nexsys-monitor/nexsys-logs-order/request-information-log/request-information-log.component';
import { ValutrustDefaultSettingComponent } from './valutrust-mapper-types/valutrust-default-setting/valutrust-default-setting.component';
import { XpanseDefaultSettingComponent } from './xpanse-mapper-types/xpanse-default-setting/xpanse-default-setting.component';
import { XpanseDefaultSettingHeaderComponent } from './xpanse-mapper-types/xpanse-default-setting/header/xpanse-default-setting-header.component';

import { FNCDefaultSettingComponent } from './fnc-mapper-types/fnc-default-setting/fnc-default-setting.component';
import { CustomHeaderComponent } from './components/custom-header/custom-header.component';
import { SaveButtonComponent } from './components/save-button/save-button.component';
import { AddButtonComponent } from './components/add-button/add-button.component';
import { MapperTypesComponent } from './components/mapper-types/mapper-types.component';
import { NotAllowedComponent } from './not-allowed/not-allowed.component';
import { GenericMapperTypesComponent } from './generic-mapper-types/generic-mapper-types/generic-mapper-types.component';
import { GenericIntegrationSelectComponent } from './generic-integration-select/generic-integration-select.component';

// Providers
import { GlobalConfigurationHttpRequestInterceptor } from './providers/global-configuration-http-request.interceptor';
import { CallCenterClientComponent } from './xpanse-mapper-types/xpanse-default-setting/call-center-clients-form/call-center-client-form';
import { FNCCallCenterClientComponent } from './fnc-mapper-types/fnc-default-setting/call-center-clients-form/call-center-client-form';
import { AcuityService } from './service/acuity.service';
import { AppScopeService } from './service/app-scope.service';
import { CIProductsService } from './service/ci-products.service';
import { DefaultSettingLoaderService } from './service/default-setting-loader.service';
import { DocumentTypesLoaderService } from './service/document-types.loader.service';
import { LoanReasonsLoaderService } from './service/loan-reasons-loader.service';
import { LoanTypesLoaderService } from './service/loan-types-loader.service';
import { OccupancyTypesLoaderService } from './service/occupancy-types-loader.service';
import { PropertyTypesLoaderService } from './service/property-types-loader.service';
import { NexsysOrderMessageService } from './nexsys-mapper-types/service/nexsys-order-message.service';
import { NexsysOrderMessageLoaderService } from './service/nexsys-order-message-loader.service';
import { ValutrustMessageLoaderService } from './service/valutrust-milestone-message-loader.service';
import { GlobalConfigurationMenuHandleService } from './service/menu-handle-sections.service';
import { MessagesComponent } from './components/messages/messages.component';
import { CustomActionDataMessageCellComponent } from './components/messages/custom-grid-cell/custom-action-data-message-cell.component';
import { CreateUpdateDataMessageTypeComponent } from './components/messages/create-update-form/create-update-data-message-type/create-update-data-message-type.component';
import { CreateUpdateMilestoneMessageComponent } from './components/messages/create-update-form/create-update-milestone-message/create-update-milestone-message.component';
import { CreateUpdateLenderCompanyMessageComponent } from './components/messages/create-update-form/create-update-lender-company-message/create-update-lender-company-message.component';
import { GenericIntegrationService } from './service/generic-integration.service';
import { GenericIntegrationState } from './generic-integrations/state/generic-integation.state';
import { GenericMapperDashboardComponent } from './generic-mapper-types/generic-mapper-dashboard.component';
import { GenericDocumentTypesComponent } from './generic-mapper-types/generic-document-types/generic-document-types.component';
import { GenericDefaultSettingsComponent } from './generic-mapper-types/generic-default-settings/generic-default-settings.component';
import { GenericMapperTypesLoaderService } from './service/generic-mapper-types.loader.service';
import { GenericEditableTableComponent } from './generic-mapper-types/generic-editable-table/generic-editable-table.component';
import { GenericHeaderComponent } from './generic-integrations/header/generic-header.component';

import { EditableTableComponent } from './components/tables/editable-table/editable-table.component';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AutoCompleteModule } from 'primeng/autocomplete';

import { ShowProductModalComponent } from './nexsys-monitor/nexsys-orders/modals/show-product-modal.component';
import { CreateUpdateAssetsComponent } from './components/messages/create-update-form/create-update-assets/create-update-assets.component';

import { AppScopeMapperComponent } from './components/app-scope-mapper/app-scope-mapper.component';
import { AppScopeMapperService } from './components/app-scope-mapper/service/app-scope-mapper.service';
import { DeliveryAssetsComponent } from './delivery-assets/delivery-assets.component';
import { DeliveryAssetsService } from "./delivery-assets/service/delivery-assets.service";
import { CmpAcuityService } from "./service/acuity-cmp.service";

import { PartialIntegrationComponent } from './partialIntegration/partial-integration.component';
import { MiMutualService } from './service/mi-mutual.service';
import { LogsPartialDescriptionComponent } from './partialIntegration/components/logs-partial-description/logs-partial-description.component';
import { RequestInformationPartialComponent } from './partialIntegration/components/request-information-partial-log/request-information-partial-log.component';
import { PartialIntegrationService } from './partialIntegration/services/data-loader-partial-integration.service';
import { RelianceFirstService } from './service/reliance-first.service';
import { CustomActionPartialOrdersCellComponent } from './partialIntegration/components/custom-action-grid-cell/custom-action-grid-cell.component';
import { OrdersPartialLogComponent } from './partialIntegration/components/orders-partial-log/orders-partial-log.component';
import { ShowPartialModalComponent } from './partialIntegration/components/modal/show-partial-modal.component';
import { PartialHeaderComponent } from './partialIntegration/components/partial-header/partial-header.component';
import { EmailListService } from './email-list/service/email-list.service';
import { EmailListComponent } from './email-list/email-list.component';
import { IntegrationListComponent } from './integrations/integrations-list.component';
import { ClientIdentifierList } from './integrations/components/client-identifier-list.component';
import { AddEditClientIdentifier } from './integrations/components/add-edit-client-identifier.component';
import { ClientIdentifierListService } from './integrations/service/client-identifier-list.service';
import { ValueLinkMonitorService } from './service/value-link.service';
import { ConnexionsMonitorService } from './service/connexions.service';
import { ConfirmationService, MessageService } from 'primeng/api';

import { OutgoingEventsHybridComponent } from './outgoing-events-hybrid/outgoing-events-hybrid.component';
import { OutgoingEventsHybridService } from './outgoing-events-hybrid/service/outgoing-events-hybrid.service';
import { EditModalComponent } from './outgoing-events-hybrid/components/edit-modal.component';
import { OutgoingEventsHybridTabsComponent } from './outgoing-events-hybrid/components/outgoing-events-hybrid-tabs/outgoing-events-hybrid-tabs.component';
import { CheckboxModule } from 'primeng/checkbox';
@NgModule({
	imports: [
		AutoCompleteModule,
		ConfirmDialogModule,
		DropdownModule,
		CheckboxModule,
		TabViewModule,
		PanelModule,
		TableModule,
		ButtonModule,
		InputTextModule,
		ToastModule,
		AceEditorModule,
		CommonModule,
		ClipboardModule,
		AgGridModule.withComponents([]),
		CvComponentLibraryModule,
		GlobalConfigurationRoutingModule,
		FormsModule,
		NgxsModule.forFeature([
			GlobalConfigurationState,
			NexsysMonitorState,
			ProductState,
			GenericIntegrationState
		]),
		HttpCoreModule,
		GenericIntegrationsModule,
		HttpCoreModule,
		ReactiveFormsModule
	],
	providers: [
		ComponentFactoryResolverService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: GlobalConfigurationHttpRequestInterceptor,
			multi: true
		},
		ProductsService,
		SoftwareCollectorPackagesService,
		LenderService,
		AcuityService,
		AppScopeService,
		CIProductsService,
		DefaultSettingLoaderService,
		DocumentTypesLoaderService,
		LoanReasonsLoaderService,
		LoanTypesLoaderService,
		GlobalConfigurationMenuHandleService,
		NexsysMonitorService,
		ValueLinkMonitorService,
		ConnexionsMonitorService,
		MiMutualService,
		RelianceFirstService,
		PartialIntegrationService,
		OccupancyTypesLoaderService,
		NexsysOrderMessageService,
		PropertyTypesLoaderService,
		NexsysOrderMessageLoaderService,
		ValutrustMessageLoaderService,
		GenericMapperTypesLoaderService,
		GenericIntegrationService,
		AppScopeMapperService,
		DeliveryAssetsService,
		EmailListService,
		OutgoingEventsHybridService,
		CmpAcuityService,
		ClientIdentifierListService,
		ConfirmationService,
		MessageService
	],
	declarations: [
		ProductsComponent,
		CreateUpdateProductsComponent,
		CustomActionProductsCellComponent,
		CallbackLogsComponent,
		PartialIntegrationComponent,
		LogsDescriptionComponent,
		LogsPartialDescriptionComponent,
		NexsysOrdersComponent,
		SoftwareCollectorPackagesComponent,
		CustomActionSoftwareCollectorPackageCellComponent,
		CreateUpdatePackageComponent,
		NexsysMonitorHeaderComponent,
		CustomActionNexsysOrdersCellComponent,
		CustomActionPartialOrdersCellComponent,
		NexsysLogsOrderComponent,
		LogsOrderDescriptionComponent,
		CustomActionRetryCellComponent,
		RetryNexsysEventComponent,
		OrdersLogComponent,
		OrdersPartialLogComponent,
		PartialHeaderComponent,
		LenderComponent,
		CustomActionLenderCellComponent,
		CustomActionNexsysOrphanReadComponent,
		CustomActionNexsysOrphanDownloadComponent,
		CustomActionNexsysHTTPStatusCodeComponent,
		CreateUpdateLenderComponent,
		CIJobTypesComponent,
		CustomActionCIJobTypesCellComponent,
		CreateUpdateCIJobTypeComponent,
		NexsysDefaultSettingComponent,
		RequestInformationComponent,
		RequestInformationPartialComponent,
		RequestInformationLogComponent,
		ValutrustDefaultSettingComponent,
		XpanseDefaultSettingComponent,
		XpanseDefaultSettingHeaderComponent,
		CallCenterClientComponent,
		CustomHeaderComponent,
		SaveButtonComponent,
		AddButtonComponent,
		MapperTypesComponent,
		FNCDefaultSettingComponent,
		FNCCallCenterClientComponent,
		NotAllowedComponent,
		MessagesComponent,
		CustomActionDataMessageCellComponent,
		CreateUpdateDataMessageTypeComponent,
		CreateUpdateMilestoneMessageComponent,
		CreateUpdateLenderCompanyMessageComponent,
		CustomActionShowProductModalCellComponent,
		ShowProductModalComponent,
		ShowPartialModalComponent,
		ScopeOrderDocumentsComponent,
		CreateUpdateAssetsComponent,
		GenericIntegrationSelectComponent,
		GenericMapperDashboardComponent,
		GenericMapperTypesComponent,
		EditableTableComponent,
		GenericDocumentTypesComponent,
		GenericDefaultSettingsComponent,
		GenericEditableTableComponent,
		CustomActionShowProductModalCellComponent,
		ScopeOrderDocumentsComponent,
		GenericMapperTypesComponent,
		GenericHeaderComponent,
		AppScopeMapperComponent,
		DeliveryAssetsComponent,
		EmailListComponent,
		OutgoingEventsHybridComponent,
		IntegrationListComponent,
		ClientIdentifierList,
		AddEditClientIdentifier,
		EditModalComponent,
		OutgoingEventsHybridTabsComponent
	],
	exports: [],
	entryComponents: [
		CreateUpdateProductsComponent,
		CustomActionProductsCellComponent,
		LogsDescriptionComponent,
		LogsPartialDescriptionComponent,
		NexsysMonitorHeaderComponent,
		CustomActionSoftwareCollectorPackageCellComponent,
		CreateUpdatePackageComponent,
		CustomActionNexsysOrdersCellComponent,
		CustomActionPartialOrdersCellComponent,
		LogsOrderDescriptionComponent,
		CustomActionRetryCellComponent,
		RetryNexsysEventComponent,
		OrdersLogComponent,
		OrdersPartialLogComponent,
		CustomActionLenderCellComponent,
		CustomActionNexsysOrphanReadComponent,
		CustomActionNexsysOrphanDownloadComponent,
		CreateUpdateLenderComponent,
		CustomActionCIJobTypesCellComponent,
		CreateUpdateCIJobTypeComponent,
		RequestInformationComponent,
		RequestInformationPartialComponent,
		RequestInformationLogComponent,
		CallCenterClientComponent,
		ClientIdentifierList,
		AddEditClientIdentifier,
		EditModalComponent,
		OutgoingEventsHybridTabsComponent
	]
})
export class GlobalConfigurationModule {}
